import { useCallback } from 'react';

import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import * as ROUTES from '#client/constants/routes';
import { getAppSection } from '#client/utils/getAppSection';

import * as Styled from './VeloCtaRow.style';
import { VELO_CTA_GRADIENT_START } from './VeloCtaRow.style';

import NoSSR from '../VeloAssistantButton/noSSR';
import VeloAvatar from '../VeloAssistantButton/VeloAvatar';

function VeloCtaRow({
  topic = 'fulfillment-status',
  promptMessageId = 'dashboard.orderTracker.velo.prompt',
  buttonMessageId = 'dashboard.orderTracker.velo.checkStatus',
  showAskVeloLabel = true,
  isGradientButton = false,
  elementName = 'order_tracker_velo_check_status',
  elementText = 'Check status',
  pageSlug = 'order_tracker',
  elementPosition = 'order_tracker',
}) {
  const theme = useTheme();
  // Amendment's VeloBanner only switches to a row layout at 'lg'; match that so the
  // Ask Velo button stays full-width at the same widths Amendment does.
  const isStacked = useMediaQuery(theme.breakpoints.down(isGradientButton ? 'lg' : 'sm'));
  const { businessEntityUuid } = useBusinessEntity();
  const captureException = useCaptureException();

  const clickstreamProps = {
    elementName,
    elementText,
    pageSlug,
    dynamicProperties: {
      ai_project_name: 'velo_chat',
    },
  };

  const storeVeloNavigationContext = useCallback(() => {
    const navigationContext = {
      referrer: window.location.pathname,
      businessEntityUuid,
      section: getAppSection(window.location.pathname) || 'order_tracker',
      timestamp: Date.now(),
      clickstreamContext: {
        elementName,
        elementPosition,
      },
    };

    try {
      sessionStorage.setItem('velo_source_context', JSON.stringify(navigationContext));
    } catch (error) {
      captureException(
        new ZBError(error, {
          message: 'Failed to store Velo navigation context',
          contextData: { details: { operation: 'storeVeloNavigationContext' } },
        }),
      );
    }
  }, [businessEntityUuid, captureException, elementName, elementPosition]);

  const handleCheckStatus = useCallback(() => {
    storeVeloNavigationContext();
    const params = new URLSearchParams({ topic });
    params.set('origination', 'dashboard-ui');
    if (pageSlug) {
      params.set('tag', pageSlug);
    }
    window.location.href = `${ROUTES.VELO_GUIDE_COMPANY.replace('[businessEntityUuid]', businessEntityUuid)}/?${params.toString()}`;
  }, [storeVeloNavigationContext, businessEntityUuid, topic, pageSlug]);

  const promptText = (
    <Text size="sm" weight={isGradientButton ? 'medium' : undefined} color={isGradientButton ? 'primary' : 'secondary'}>
      <FormattedMessage id={promptMessageId} />
    </Text>
  );

  const veloAvatarElement = (
    <Box display="flex" alignItems="center">
      <NoSSR>
        <VeloAvatar state="default" width={24} height={24} color={VELO_CTA_GRADIENT_START} />
      </NoSSR>
    </Box>
  );

  const askVeloText = (
    <Text size="sm" weight="medium" color="primary">
      <FormattedMessage id="dashboard.orderTracker.velo.askVelo" />
    </Text>
  );

  const ctaButton = isGradientButton ? (
    <Styled.AskVeloButtonWrapper sx={{ width: isStacked ? '100%' : 'auto' }}>
      <Styled.AskVeloButton
        fullWidth={isStacked}
        clickstreamProps={clickstreamProps}
        onClick={handleCheckStatus}
        data-testid="velo-cta-button"
      >
        <FormattedMessage id={buttonMessageId} />
      </Styled.AskVeloButton>
    </Styled.AskVeloButtonWrapper>
  ) : (
    <Button
      variant="contained"
      color="primary"
      size="small"
      clickstreamProps={clickstreamProps}
      onClick={handleCheckStatus}
      data-testid="velo-cta-button"
    >
      <FormattedMessage id={buttonMessageId} />
    </Button>
  );

  const RowComponent = isGradientButton ? Styled.ProductPageVeloCtaBackground : Styled.VeloCtaBackground;

  return (
    <RowComponent data-testid="velo-cta-row">
      <Stack
        direction={isStacked ? 'column' : 'row'}
        alignItems={isStacked ? 'flex-start' : 'center'}
        justifyContent={isStacked ? 'flex-start' : 'space-between'}
        spacing={1}
      >
        {isStacked ? (
          <>
            {promptText}
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              {showAskVeloLabel && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {veloAvatarElement}
                  {askVeloText}
                </Stack>
              )}
              {ctaButton}
            </Stack>
          </>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              {promptText}
              {showAskVeloLabel && (
                <>
                  {veloAvatarElement}
                  {askVeloText}
                </>
              )}
            </Stack>
            {ctaButton}
          </>
        )}
      </Stack>
    </RowComponent>
  );
}

VeloCtaRow.propTypes = {
  topic: PropTypes.string,
  promptMessageId: PropTypes.string,
  buttonMessageId: PropTypes.string,
  showAskVeloLabel: PropTypes.bool,
  isGradientButton: PropTypes.bool,
  elementName: PropTypes.string,
  elementText: PropTypes.string,
  pageSlug: PropTypes.string,
  elementPosition: PropTypes.string,
};

export default VeloCtaRow;
